import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { shipmentsAPI } from '../services/api';
import { MapPin, Edit, MessageCircle, Calendar, User, Package, DollarSign, Truck, MessageSquare, Clock, Trash2, Paperclip } from 'lucide-react';
import toast from 'react-hot-toast';
import UserDropdown from '../components/UserDropdown';
import AttachmentUpload from '../components/AttachmentUpload';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const ShipmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery(['shipment', id], () => shipmentsAPI.getById(id));
  const [commentText, setCommentText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [activeTab, setActiveTab] = useState('comments');
  const [newComment, setNewComment] = useState('');
  const tabsRef = useRef(null);

  // Add comment mutation
  const addCommentMutation = useMutation(
    (text) => shipmentsAPI.addComment(id, text),
    {
      onSuccess: () => {
        setCommentText('');
        queryClient.invalidateQueries(['shipment', id]);
        toast.success('Comment added successfully');
      }
    }
  );

  // Edit comment mutation
  const editCommentMutation = useMutation(
    ({ commentId, text }) => shipmentsAPI.editComment(id, commentId, text),
    {
      onSuccess: () => {
        setEditingCommentId(null);
        setEditingCommentText('');
        queryClient.invalidateQueries(['shipment', id]);
        toast.success('Comment updated successfully');
      }
    }
  );

  // Delete comment mutation
  const deleteCommentMutation = useMutation(
    (commentId) => shipmentsAPI.deleteComment(id, commentId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['shipment', id]);
        toast.success('Comment deleted successfully');
      }
    }
  );

  // Update shipment status mutation
  const updateShipmentStatusMutation = useMutation(
    (newStatus) => shipmentsAPI.updateStatus(id, newStatus),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['shipment', id]);
        toast.success('Shipment status updated successfully');
      }
    }
  );

  // Update shipment status mutation
  const updateShipmentStatusMutation2 = useMutation(
    (newStatus) => shipmentsAPI.updateShipmentStatus(id, newStatus),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['shipment', id]);
        toast.success('Shipment status updated successfully');
      }
    }
  );

  // Update payment status mutation
  const updatePaymentStatusMutation = useMutation(
    (newStatus) => shipmentsAPI.updatePaymentStatus(id, newStatus),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['shipment', id]);
        toast.success('Payment status updated successfully');
      }
    }
  );

  // Assign user mutation
  const assignUserMutation = useMutation(
    (userIds) => shipmentsAPI.assignUsers(id, userIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['shipment', id]);
        toast.success('User reassigned successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to reassign user');
      }
    }
  );

  const handleUserChange = (userId) => {
    if (userId) {
      assignUserMutation.mutate([userId]);
    }
  };

  if (isLoading) return <div className="p-8 text-center">Loading...</div>;
  if (error) return <div className="p-8 text-center text-red-600">Error loading shipment</div>;
  if (!data) return <div className="p-8 text-center">Shipment not found</div>;

  const { shipment, boxRequirements, statusHistory, comments, attachments, assignedUsers } = data;

  // Function to generate proper Google Maps URL
  const generateGoogleMapsUrl = (location) => {
    if (!location) return null;
    let loc = location.trim();
    if (loc.startsWith('@')) loc = loc.slice(1);

    // If it's coordinates (lat, lng format), wrap in a search URL
    if (loc.match(/^(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)$/)) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc)}`;
    }

    // Otherwise, just return the value as-is (URL or text)
    return loc;
  };

  // Function to generate Google Maps embed URL
  const generateMapEmbedUrl = (location) => {
    if (!location) return null;
    
    // Check if it's already a Google Maps URL - extract coordinates
    if (location.includes('maps.google.com') || location.includes('maps.app.goo.gl')) {
      // Extract coordinates from Google Maps URL
      const coordMatch = location.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (coordMatch) {
        const lat = coordMatch[1];
        const lng = coordMatch[2];
        return `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${lat},${lng}`;
      }
    }
    
    // Check if it's coordinates (lat, lng format)
    if (location.includes(',') && !isNaN(parseFloat(location.split(',')[0]))) {
      return `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(location)}`;
    }
    
    // Otherwise treat as a search query
    return `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(location)}`;
  };

  // Google Maps Embed URL
  let mapUrl = generateMapEmbedUrl(shipment.pickup_location);

  // Assume current user info is available in localStorage (or context)
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'fully_paid': return 'bg-green-100 text-green-800';
      case 'partially_paid': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getShipmentStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'in_transit': return 'bg-blue-100 text-blue-800';
      case 'in_shipping': return 'bg-blue-100 text-blue-800';
      case 'in_sl_warehouse': return 'bg-purple-100 text-purple-800';
      case 'shipping_to_customer': return 'bg-orange-100 text-orange-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Shipment status options with workflow
  const shipmentStatusOptions = [
    { value: 'inquiry', label: 'Inquiry', color: 'bg-gray-100 text-gray-800' },
    { value: 'box_collection', label: 'Box Collection', color: 'bg-blue-100 text-blue-800' },
    { value: 'box_collection_done', label: 'Box Collection Done', color: 'bg-green-100 text-green-800' },
    { value: 'box_delivery', label: 'Box Delivery', color: 'bg-blue-100 text-blue-800' },
    { value: 'box_delivery_done', label: 'Box Delivery Done', color: 'bg-green-100 text-green-800' },
    { value: 'quick_packing', label: 'Quick Packing', color: 'bg-blue-100 text-blue-800' },
    { value: 'quick_packing_done', label: 'Quick Packing Done', color: 'bg-green-100 text-green-800' },
    { value: 'shipment', label: 'Ready for Shipment', color: 'bg-purple-100 text-purple-800' }
  ];

  const shipmentStatusOptions2 = [
    { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'in_shipping', label: 'In Shipping', color: 'bg-blue-100 text-blue-800' },
    { value: 'in_sl_warehouse', label: 'In SL Warehouse', color: 'bg-purple-100 text-purple-800' },
    { value: 'shipping_to_customer', label: 'Shipping to Customer', color: 'bg-orange-100 text-orange-800' },
    { value: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-800' }
  ];

  const paymentStatusOptions = [
    { value: 'pending', label: 'Pending', color: 'bg-red-100 text-red-800' },
    { value: 'partial_paid', label: 'Partial Paid', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'fully_paid', label: 'Fully Paid', color: 'bg-green-100 text-green-800' }
  ];

  // Business logic for status editing
  const getAvailableShipmentStatuses = () => {
    const currentStatus = shipment.status;
    
    // Define the restricted transitions (from Done statuses back to their previous status)
    const restrictedTransitions = {
      'box_collection_done': 'box_collection',
      'box_delivery_done': 'box_delivery', 
      'quick_packing_done': 'quick_packing'
    };
    
    // Filter out the restricted option based on current status
    return shipmentStatusOptions.filter(option => {
      const restrictedOption = restrictedTransitions[currentStatus];
      return option.value !== restrictedOption;
    });
  };

  const canEditShipmentStatus = shipment.status === 'shipment';
  const canEditPaymentStatus = true;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shipment #{shipment.id}</h1>
          <p className="text-gray-600">Detailed view of shipment</p>
        </div>
        <button
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          onClick={() => navigate(`/shipments/${shipment.id}/edit`)}
        >
          <Edit className="h-4 w-4 mr-2" />
          Edit Shipment
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Shipment Details Card */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Package className="h-5 w-5 mr-2" />
                Shipment Details
              </h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center">
                    <User className="h-4 w-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Customer</p>
                      <p className="font-medium">{shipment.customer_name}</p>
                      <p className="text-sm text-gray-600">{shipment.customer_phone}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <MapPin className="h-4 w-4 text-gray-400 mr-3 mt-1" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Pickup Address</p>
                      <p className="font-medium break-words">{shipment.pickup_address}</p>
                      {shipment.pickup_location && (
                        <div className="mt-1">
                          <p className="text-sm text-gray-500">Location:</p>
                          <a
                            href={generateGoogleMapsUrl(shipment.pickup_location)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            Click here
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Truck className="h-4 w-4 text-gray-400 mr-3 mt-1" />
                    <div>
                      <p className="text-sm text-gray-500">Delivery Address</p>
                      <p className="font-medium">{shipment.delivery_address}</p>
                    </div>
                  </div>

                  <div className="flex items-center mt-2">
                    <User className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-500 mr-1">Created By</span>
                    <span className="font-medium text-gray-900">{shipment.created_by_name || 'Unknown'}</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">City & Route</p>
                    <p className="font-medium">{shipment.city_name} • {shipment.route_name}</p>
                  </div>
                  
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Delivery Date</p>
                      <p className="font-medium">
                        {shipment.delivery_date ? new Date(shipment.delivery_date).toLocaleDateString() : 'Not set'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Due Date</p>
                      <p className="font-medium">
                        {shipment.due_date ? new Date(shipment.due_date).toLocaleDateString() : 'Not set'}
                      </p>
                    </div>
                  </div>

                  {/* Attachment Indicator */}
                  <div className="flex items-center">
                    <Paperclip className="h-4 w-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Attachments</p>
                      <button
                        onClick={() => {
                          setActiveTab('attachments');
                          if (tabsRef.current) {
                            tabsRef.current.scrollIntoView({ behavior: 'smooth' });
                          }
                        }}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                      >
                        {attachments.length} attachment{attachments.length !== 1 ? 's' : ''} available
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Map */}
          {mapUrl && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <MapPin className="h-5 w-5 mr-2" />
                  Pickup Location
                </h3>
              </div>
              <div className="p-6">
                <iframe
                  title="Pickup Location Map"
                  width="100%"
                  height="300"
                  frameBorder="0"
                  style={{ border: 0, borderRadius: '8px' }}
                  src={mapUrl}
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          )}

          {/* Box Requirements */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Package className="h-5 w-5 mr-2" />
                Box Requirements
              </h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {boxRequirements.map((box, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">{box.box_type}</span>
                    <span className="text-sm text-gray-600">Qty: {box.quantity}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Description */}
          {shipment.description && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <MessageSquare className="h-5 w-5 mr-2" />
                  Description
                </h3>
              </div>
              <div className="p-6">
                <div className="prose max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap">{shipment.description}</p>
                </div>
              </div>
            </div>
          )}

          {/* Comments and Change History Tabs */}
          <div className="bg-white rounded-lg shadow" ref={tabsRef}>
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('comments')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'comments'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <MessageSquare className="h-4 w-4" />
                  <span>Comments</span>
                </button>
                <button
                  onClick={() => setActiveTab('attachments')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'attachments'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Paperclip className="h-4 w-4" />
                  <span>Attachments</span>
                  {attachments.length > 0 && (
                    <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                      {attachments.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'history'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Clock className="h-4 w-4" />
                  <span>Change History</span>
                </button>
              </div>
            </div>
            <div className="p-6">
              {activeTab === 'comments' && (
                <div>
                  <form
                    onSubmit={e => {
                      e.preventDefault();
                      if (editingCommentId) {
                        editCommentMutation.mutate({ commentId: editingCommentId, text: editingCommentText });
                      } else {
                        addCommentMutation.mutate(commentText);
                      }
                    }}
                    className="mb-4"
                  >
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
                      rows="3"
                      placeholder="Add a comment..."
                      value={editingCommentId ? editingCommentText : commentText}
                      onChange={e => {
                        if (editingCommentId) setEditingCommentText(e.target.value);
                        else setCommentText(e.target.value);
                      }}
                      required
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        type="submit"
                        className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                        disabled={addCommentMutation.isLoading || editCommentMutation.isLoading}
                      >
                        {editingCommentId ? 'Update' : 'Add Comment'}
                      </button>
                      {editingCommentId && (
                        <button
                          type="button"
                          className="px-3 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 text-sm"
                          onClick={() => {
                            setEditingCommentId(null);
                            setEditingCommentText('');
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </form>

                  <div className="space-y-3">
                    {comments.length > 0 ? comments.map((comment, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="font-medium text-sm">{comment.user_name}</span>
                              <span className="text-xs text-gray-500">
                                {new Date(comment.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700">{comment.text}</p>
                          </div>
                          {comment.user_id === currentUser.id && (
                            <div className="flex gap-1">
                              <button
                                className="text-blue-600 hover:underline text-xs"
                                onClick={() => {
                                  setEditingCommentId(comment.id);
                                  setEditingCommentText(comment.text);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                className="text-red-600 hover:underline text-xs"
                                onClick={() => deleteCommentMutation.mutate(comment.id)}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )) : (
                      <p className="text-gray-500 text-center py-4">No comments yet</p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'attachments' && (
                <div className="space-y-4">
                  <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <div className="mb-2">
                      <span className="text-sm text-gray-500">Created By</span>
                      <div className="font-medium text-gray-900">{shipment.created_by_name || 'Unknown'}</div>
                    </div>
                    <AttachmentUpload 
                      shipmentId={shipment.id}
                      attachments={attachments}
                      onUploadSuccess={(newAttachments) => {
                        // Update the query cache with new attachments
                        queryClient.setQueryData(['shipment', id], (oldData) => ({
                          ...oldData,
                          attachments: newAttachments
                        }));
                      }}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'history' && (
                <div className="space-y-3">
                  {statusHistory.map((item, idx) => {
                    // Handle attachment history
                    if (item.history_type === 'attachment') {
                      return (
                        <div key={idx} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              File {item.action}: {item.filename}
                            </p>
                            <p className="text-xs text-gray-600">
                              by {item.user_name} on {new Date(item.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      );
                    }

                    // Handle assignment change history
                    const isAssignmentChange = item.from_status?.startsWith('assigned_user:') || item.to_status?.startsWith('assigned_user:');
                    
                    if (isAssignmentChange) {
                      // Parse assignment change format: assigned_user:userId:userName
                      const fromParts = item.from_status?.split(':') || [];
                      const toParts = item.to_status?.split(':') || [];
                      
                      const fromUserName = fromParts.length >= 3 ? fromParts[2] : (fromParts[1] === 'null' ? 'No user' : 'Unknown user');
                      const toUserName = toParts.length >= 3 ? toParts[2] : (toParts[1] === 'null' ? 'No user' : 'Unknown user');
                      
                      return (
                        <div key={idx} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              User assignment changed
                            </p>
                            <p className="text-xs text-gray-600">
                              {fromUserName} → {toUserName}
                            </p>
                            <p className="text-xs text-gray-500">
                              by {item.changed_by_name} on {new Date(item.created_at).toLocaleString()}
                            </p>
                            {item.comment && (
                              <p className="text-xs text-gray-500 mt-1">"{item.comment}"</p>
                            )}
                          </div>
                        </div>
                      );
                    }

                    // Handle regular status changes
                    return (
                      <div key={idx} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">
                            {item.from_status || 'N/A'} → {item.to_status}
                          </p>
                          <p className="text-xs text-gray-600">
                            by {item.changed_by_name} on {new Date(item.created_at).toLocaleString()}
                          </p>
                          {item.comment && (
                            <p className="text-xs text-gray-500 mt-1">"{item.comment}"</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Shipment Status */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Package className="h-5 w-5 mr-2" />
                Shipment Status
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Status
                  </label>
                  <div className="relative">
                    <select
                      value={shipment.status || ''}
                      onChange={(e) => updateShipmentStatusMutation.mutate(e.target.value)}
                      className="w-full px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-900 font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none cursor-pointer"
                      disabled={updateShipmentStatusMutation.isLoading}
                    >
                      {getAvailableShipmentStatuses().map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Update the shipment workflow status
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Shipment Status */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Truck className="h-5 w-5 mr-2" />
                Shipment Status
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Status
                  </label>
                  <div className="relative">
                    <select
                      value={shipment.shipment_status || ''}
                      onChange={(e) => updateShipmentStatusMutation2.mutate(e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none cursor-pointer ${
                        canEditShipmentStatus 
                          ? 'bg-blue-50 border-blue-200 text-blue-900' 
                          : 'bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed'
                      }`}
                      disabled={!canEditShipmentStatus || updateShipmentStatusMutation2.isLoading}
                    >
                      {shipmentStatusOptions2.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className={`h-5 w-5 ${canEditShipmentStatus ? 'text-blue-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    {canEditShipmentStatus 
                      ? 'Update the shipment status to track progress' 
                      : 'Shipment status available when shipment is ready for shipment'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Status */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <DollarSign className="h-5 w-5 mr-2" />
                Payment Status
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Status
                  </label>
                  <div className="relative">
                    <select
                      value={shipment.shipment_payment_status || ''}
                      onChange={(e) => updatePaymentStatusMutation.mutate(e.target.value)}
                      className="w-full px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-900 font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none cursor-pointer"
                      disabled={updatePaymentStatusMutation.isLoading}
                    >
                      {paymentStatusOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Update the payment status to track financial progress
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Assigned Users */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Assigned User</h3>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Assignment
                  </label>
                  <UserDropdown
                    value={assignedUsers.length > 0 ? assignedUsers[0].id : null}
                    onChange={handleUserChange}
                    placeholder="Select user to assign..."
                    disabled={assignUserMutation.isLoading}
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    Search and select a user to assign to this shipment
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShipmentDetail; 